import log from "loglevel";
import { base_url } from 'Constants';
import httpService from 'services/httpService';
import { CKEditor } from 'ckeditor4-react';
import React, { useEffect, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap';

const editorUrl = `${base_url}/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js`;
log.log({ editorUrl });

const CKEditorLoading = () => <div className='d-flex gap-2 h-100 align-items-center'><Spinner animation='border' size='sm' /> <small>Loading...</small></div>

const editorConfig = {
    enterMode: 2, // CKEDITOR.ENTER_BR - use <br> instead of <p> for less line spacing
    shiftEnterMode: 1, // CKEDITOR.ENTER_P - Shift+Enter creates paragraph
    extraPlugins: [
        "uploadimage",
        // "mathjax",
        "ckeditor_wiris",
        "texzilla",
        "exportpdf",
        "codesnippet",
        "image2",
        "imageresizerowandcolumn",
        "markdown",
        // "custom_save", // Commented out - plugin not available in dev environment
        "pastefromgdocs"
    ],
    // skin: 'moono-dark',
    toolbar: [
        { name: 'document', items: ['Source', 'Save', 'Markdown'] },
        { name: 'styles', items: ['Format', 'FontSize', 'Font'] },
        { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
        { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'] },
        { name: 'links', items: ['Link'] },
        { name: 'insert', items: ['Image', 'image2', 'Table', 'HorizontalRule', 'texzilla', 'CodeSnippet'] },
        { name: 'colors', items: ['TextColor', 'BGColor'] },
        { name: 'tools', items: ['Maximize'] },
        { name: 'about', items: ['About'] },
        // { name: 'others', items: ['ckeditor_wiris_formulaEditor', 'ckeditor_wiris_formulaEditorChemistry']}
    ],
    removePlugins: ["image", "exportpdf", "forms", "smiley", "language", "iframe", "about", "emoji", 'katex'],
    filebrowserImageUploadUrl: base_url + `/util/uploaduserfiles/`,
    uploadUrl: base_url + `/util/uploaduserfiles/`,
    contentsCss: [
        base_url + "/staticfiles/ckeditor/4.19.0/full-all/document-style.css",
        // Unified CSS for WYSIWYG - ensures instructor sees what student sees
        window.location.origin + "/styles/ckeditor-content.css"
    ],
    bodyClass: "document-editor",
    // height: "15.8cm",
    width: "100%",
    // Allow all content and formatting to preserve source formatting from Google Docs/Word
    pasteFilter: null, // Allow all pasted content
    allowedContent: true, // Allow all content and attributes
    disallowedContent: '', // Don't disallow any content
    // Force CKEditor to output semantic tags for bold/italic/underline
    coreStyles_bold: { element: 'strong' },
    coreStyles_italic: { element: 'em' },
    coreStyles_underline: { element: 'u' },
    protectedSource: [/{%[\s\S]*?%}+/g, /{{[\s\S]*?}}+/g],
    mathJaxLib: '/assets/mathjax/MathJax.js?config=TeX-AMS_HTML',
    config: {
        // MathJax configuration
        tex2jax: {
            inlineMath: [['$', '$'], ['\\(', '\\)']],
            displayMath: [['$$', '$$'], ['\\[', '\\]']],
            processEscapes: true
        }
    },
    katexLibCss: '',
    katexLibJs: '',
    // formatOutput: false,
    // formatSource: false
    font_defaultLabel: 'Serif'
};

export const resetCkEditorInstanceData = (instanceName) => {
    /*
        @params: instanceName: name of the instance (name) attribute passed to CKEditor instance
    */
    if (!instanceName || typeof instanceName !== "string") {
        return;
    }

    // Check if CKEDITOR is loaded before accessing instances
    if (!window.CKEDITOR || !window.CKEDITOR.instances) {
        log.log('CKEditor not loaded yet, skipping reset');
        return;
    }

    log.log({ instances: window.CKEDITOR.instances, instanceName });
    window.CKEDITOR.instances[instanceName]?.setData?.('');
}

// Export cleanHtmlForSave so it can be used by parent components before saving to backend
export const cleanHtmlForSave = (html) => {
    if (!html) return html;

    // The editor's onChange emits { html, text }, so callers may pass the object
    // directly. Accept either shape.
    if (typeof html === 'object') {
        html = html.html || html.text || '';
    }
    if (typeof html !== 'string') return html;

    // Remove empty paragraphs with just &nbsp;
    html = html.replace(/<p[^>]*>\s*&nbsp;\s*<\/p>/gi, '');

    // Remove empty paragraphs
    html = html.replace(/<p[^>]*>\s*<\/p>/gi, '');

    // Remove multiple consecutive line breaks
    html = html.replace(/(<br\s*\/?>){2,}/gi, '<br>');

    // Remove paragraphs that only contain a line break
    html = html.replace(/<p[^>]*>\s*<br\s*\/?>\s*<\/p>/gi, '');

    return html;
};

const CustomCKEditor = ({
    onBlurAction, previousResponseText, preview, forwardedRef, placeholder, name, defaultValue, onChange, value, disabled, config: customConfig, onReadyChange
}) => {

    // log.log({ value, defaultValue, previousResponseText })
    const [loading, setLoading] = useState(true);
    const [editorContent, setEditorContent] = useState("");
    const [editorReady, setEditorReady] = useState(false);
    const [initFailed, setInitFailed] = useState(false);
    const [slowLoading, setSlowLoading] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const valueRef = useRef(value);
    const editorRef = useRef(null);
    const isProgrammaticUpdate = useRef(false);
    const initTimerRef = useRef(null);

    useEffect(() => {
        log.log("Script ck-editor mounted")
        setLoading(true);
        // Small delay to ensure DOM is ready
        const timer = setTimeout(() => setLoading(false), 100);
        return () => clearTimeout(timer);
    }, [retryCount]);

    // Show "taking longer than expected" notice after 15s without unmounting
    // the editor — on slow student networks CKEditor just needs more time.
    // If window.CKEDITOR is still missing at 15s, treat it as a hard load failure.
    useEffect(() => {
        if (loading) return;
        setSlowLoading(false);

        initTimerRef.current = setTimeout(() => {
            if (editorRef.current) return;
            if (!window.CKEDITOR) {
                setInitFailed(true);
                log.warn('[CKEditor] Script not loaded after 15s — treating as load failure');
            } else {
                setSlowLoading(true);
                log.warn('[CKEditor] Still loading after 15s — showing slow-load notice');
            }
        }, 15000);

        return () => {
            if (initTimerRef.current) {
                clearTimeout(initTimerRef.current);
                initTimerRef.current = null;
            }
        };
    }, [loading, retryCount]);

    // Update editor content when value prop changes
    useEffect(() => {
        if (editorRef.current && value !== undefined && value !== null) {
            const currentData = editorRef.current.getData();
            const newValue = typeof value === 'object' ? (value.html || value.text || '') : value;

            // Smart comparison: clean both sides before comparing to avoid cursor reset
            // from whitespace/empty paragraph differences
            const cleanedCurrentData = cleanHtmlForSave(currentData);
            const cleanedNewValue = cleanHtmlForSave(newValue);

            // Only update if the cleaned values are meaningfully different
            if (cleanedCurrentData !== cleanedNewValue) {
                // Set flag to prevent onChange from firing
                isProgrammaticUpdate.current = true;
                editorRef.current.setData(newValue);
                // Reset flag after setData completes (async operation)
                setTimeout(() => {
                    isProgrammaticUpdate.current = false;
                }, 100);
            }
        }
    }, [value]);

    const handleEditorReady = (event) => {
        if (event.editor) {
            // Store editor reference as soon as it's ready
            editorRef.current = event.editor;

            // Verify editor is actually functional (editable area rendered)
            // CKEditor can "initialize" from cached script but fail to render its UI
            setTimeout(() => {
                const editable = event.editor.editable();
                if (!editable || !editable.$) {
                    log.warn('[CKEditor] Editor initialized but editable area not ready — showing retry button');
                    setInitFailed(true);
                    editorRef.current = null;
                    return;
                }
                // Editor is truly ready — clear init timeout
                if (initTimerRef.current) {
                    clearTimeout(initTimerRef.current);
                    initTimerRef.current = null;
                }
                setInitFailed(false);
                setSlowLoading(false);
                setEditorReady(true);
                if (typeof onReadyChange === 'function') onReadyChange(true);
            }, 2000);

            // Note: CSS styling is now handled by the unified ckeditor-content.css file
            // loaded via contentsCss configuration. This ensures WYSIWYG between
            // instructor editor and student view.

            // Attach the current Authorization header at upload time. We can't use
            // the static fileTools_requestHeaders config because it captures the
            // header object at module-load time, before login has populated it.
            event.editor.on('fileUploadRequest', function (evt) {
                const xhr = evt.data?.fileLoader?.xhr;
                const auth = httpService.defaults.headers.common?.Authorization;
                if (auth && xhr) {
                    xhr.setRequestHeader('Authorization', auth);
                }
            });

            // The uploadwidget plugin's downcast returns "" while the image is in
            // its uploading-widget state, so editor.getData() returns no <img>
            // until the widget is swapped for a real <img> via replaceWith() in
            // the uploadimage plugin's onUploaded handler. That swap is
            // programmatic and doesn't fire CKEditor's `change` event, so our
            // onChange handler never sees the final HTML and the description
            // ends up saved without the image. Re-emit onChange a tick after the
            // upload response so the swapped DOM gets serialized.
            event.editor.on('fileUploadResponse', function () {
                setTimeout(() => {
                    if (!editorRef.current) return;
                    const data = editorRef.current.getData();
                    setEditorContent(data);
                    if (!isProgrammaticUpdate.current && typeof onChange === 'function') {
                        onChange({ html: data, text: data });
                    }
                }, 50);
            });

            // Paste handler - minimal cleanup + convert text dashes to proper <hr> tags
            event.editor.on('paste', function(evt) {
                if (evt.data && evt.data.dataValue) {
                    const originalHtml = evt.data.dataValue;

                    // Log summary for debugging
                    log.log(`[CKEditor Paste] Processing ${originalHtml.length} characters, contains margin-left: ${originalHtml.includes('margin-left')}`);

                    try {
                        // Only do minimal cleanup - preserve all formatting
                        let cleanedHtml = originalHtml;

                        // Convert paragraphs with only dashes (—, –, -, _) to proper <hr> tags
                        // This handles Google Docs dividers that are pasted as text
                        const tempDiv = document.createElement('div');
                        tempDiv.innerHTML = cleanedHtml;

                        // Find all paragraphs
                        const paragraphs = tempDiv.querySelectorAll('p');
                        paragraphs.forEach(p => {
                            // Get text content, removing all HTML tags
                            const textContent = p.textContent || '';
                            // Check if paragraph contains only dashes/underscores (at least 3 characters)
                            const dashPattern = /^[\s\u2014\u2013\-_]+$/;
                            if (textContent.length >= 3 && dashPattern.test(textContent)) {
                                // Replace paragraph with <hr> tag
                                const hr = document.createElement('hr');
                                p.parentNode.replaceChild(hr, p);
                                log.log('[CKEditor Paste] Converted text dashes to <hr>');
                            }
                        });

                        cleanedHtml = tempDiv.innerHTML;

                        // Note: Do NOT try to continue list numbering across separate lists
                        // Different sections (Part A, Part B, etc.) are separate lists that should each start at 1

                        // Only merge truly consecutive lists (with only whitespace/empty paragraphs between)
                        // This handles cases where Google Docs splits a single logical list
                        cleanedHtml = cleanedHtml.replace(/<\/ol>\s*(?:<p[^>]*>\s*(?:&nbsp;)?\s*<\/p>\s*)*<ol[^>]*>/gi, '\n');
                        cleanedHtml = cleanedHtml.replace(/<\/ul>\s*(?:<p[^>]*>\s*(?:&nbsp;)?\s*<\/p>\s*)*<ul[^>]*>/gi, '\n');

                        // Only remove excessive empty paragraphs (3+ consecutive)
                        cleanedHtml = cleanedHtml.replace(/(<p[^>]*>\s*(&nbsp;)?\s*<\/p>\s*){3,}/gi, '<p>&nbsp;</p><p>&nbsp;</p>');

                        // Only remove excessive breaks (3+ consecutive)
                        cleanedHtml = cleanedHtml.replace(/(<br\s*\/?>){3,}/gi, '<br><br>');

                        evt.data.dataValue = cleanedHtml;

                        log.log(`[CKEditor Paste] Preserved formatting: ${originalHtml.length} → ${cleanedHtml.length} chars`);
                    } catch (error) {
                        log.error('[CKEditor Paste] Cleanup failed:', error);
                        // Use original HTML if cleanup fails
                    }
                }
            }, null, null, 5);  // Priority 5 - BEFORE default handlers to preserve formatting

            // Removed getData filter to preserve all formatting including styled spans

            // Add key event listener to clean empty paragraphs after delete/backspace
            event.editor.on('key', function(evt) {
                // Check if backspace or delete was pressed
                if (evt.data.keyCode === 8 || evt.data.keyCode === 46) {
                    // Clean empty paragraphs after a short delay
                    setTimeout(() => {
                        const editable = event.editor.editable();
                        if (editable && editable.$) {
                            const emptyParas = editable.$.querySelectorAll('p');
                            emptyParas.forEach(p => {
                                const text = p.textContent.trim();
                                const hasChildren = p.children.length > 0;
                                if (!text && !hasChildren) {
                                    p.remove();
                                }
                            });
                        }
                    }, 50);
                }
            });

            // Set initial value if provided
            const initialValue = value || previousResponseText || defaultValue;
            if (initialValue) {
                const valueToSet = typeof initialValue === 'object'
                    ? (initialValue.html || initialValue.text || '')
                    : initialValue;

                if (valueToSet) {
                    // Set flag to prevent onChange from firing during initialization
                    isProgrammaticUpdate.current = true;
                    event.editor.setData(valueToSet);
                    // Reset flag after setData completes (async operation)
                    setTimeout(() => {
                        isProgrammaticUpdate.current = false;
                    }, 100);
                }
            }
        }
    };

    const handleEditorChange = (event) => {
        if (event.editor) {
            const data = event.editor.getData();
            // Don't clean here - let raw HTML flow to parent to prevent cursor reset
            // Cleaning will happen before saving to backend
            setEditorContent(data);

            // Only call onChange if this is a user edit, not a programmatic update
            if (!isProgrammaticUpdate.current && typeof onChange === "function") {
                onChange({ html: data, text: data });
            }
        }
    }

    const handleBlurEvent = (event) => {
        if (event.editor) {
            const data = event.editor.getData();
            // Clean the HTML before passing to onBlur handler
            const cleanedData = cleanHtmlForSave(data);
            // log.log(cleanedData);
            return typeof onBlurAction === 'function' ? onBlurAction(cleanedData) : null;
        }
    }

    // Keep valueRef updated
    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    useEffect(() => {
        return () => {
            if (window.CKEDITOR?.instances?.[name]) {
                window.CKEDITOR.instances[name].destroy();
            }
        };
    }, [name]);

    // Sync editor content when value prop changes (only if editor is ready)
    useEffect(() => {
        if (editorReady && window.CKEDITOR?.instances?.[name] && value !== undefined && value !== null) {
            const editor = window.CKEDITOR.instances[name];
            const currentData = editor.getData();
            const newValue = typeof value === 'object' ? (value.html || value.text || '') : value;

            // Only update if value has actually changed to prevent cursor jumping
            if (currentData !== newValue && newValue !== '') {
                editor.setData(newValue);
            }
        }
    }, [value, name, editorReady]);

    const handleRetry = () => {
        // Destroy any existing broken instance
        if (window.CKEDITOR?.instances?.[name]) {
            try { window.CKEDITOR.instances[name].destroy(); } catch (e) { /* ignore */ }
        }
        editorRef.current = null;
        setEditorReady(false);
        setSlowLoading(false);
        if (typeof onReadyChange === 'function') onReadyChange(false);

        if (!window.CKEDITOR) {
            // Script never loaded — load it manually BEFORE mounting CKEditor.
            // Keep initFailed=true (retry banner stays visible with spinner)
            // so ckeditor4-react doesn't try to mount and use its cached failed promise.
            setRetrying(true);
            const script = document.createElement('script');
            script.src = editorUrl;
            script.onload = () => {
                log.log('[CKEditor] Script loaded manually on retry');
                // Now window.CKEDITOR exists — safe to mount CKEditor component.
                // ckeditor4-react will see window.CKEDITOR and use it directly.
                // IMPORTANT: Set loading=true here so CKEditor doesn't briefly mount
                // before the retryCount useEffect runs. Without this, React batches
                // initFailed=false + retryCount change into one render where loading
                // is still false, causing CKEditor to mount and call CKEDITOR.replace().
                // Then the effect sets loading=true, unmounting the DOM element mid-
                // initialization, which crashes with "Cannot read properties of null".
                setRetrying(false);
                setInitFailed(false);
                setLoading(true);
                setRetryCount(prev => prev + 1);
            };
            script.onerror = () => {
                log.warn('[CKEditor] Script failed to load on retry');
                setRetrying(false);
                setInitFailed(true);
            };
            document.head.appendChild(script);
        } else {
            // Script already loaded — just remount the CKEditor component
            setInitFailed(false);
            setRetryCount(prev => prev + 1);
        }
    };

    if (loading) return <CKEditorLoading />;

    // Merge custom config with default editorConfig
    const finalConfig = customConfig ? { ...editorConfig, ...customConfig } : editorConfig;

    if (initFailed) {
        return (
            <div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#856404'
                }}>
                    <span>Editor failed to load. This may be due to a slow connection.</span>
                    <button
                        onClick={handleRetry}
                        disabled={retrying}
                        style={{
                            marginLeft: '12px',
                            padding: '6px 16px',
                            fontSize: '14px',
                            backgroundColor: retrying ? '#6c757d' : '#0d6efd',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: retrying ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        {retrying && <Spinner animation="border" size="sm" />}
                        {retrying ? 'Retrying...' : 'Retry'}
                    </button>
                </div>
                <input name={name} type='hidden' value={editorContent} onChange={null} />
            </div>
        );
    }

    const slowLoadingBanner = (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            marginTop: '8px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#856404'
        }}>
            <span>Editor is still loading — this can take a while on slow connections.</span>
            <button
                onClick={handleRetry}
                disabled={retrying}
                style={{
                    marginLeft: '12px',
                    padding: '6px 16px',
                    fontSize: '14px',
                    backgroundColor: retrying ? '#6c757d' : '#0d6efd',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: retrying ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
            >
                {retrying && <Spinner animation="border" size="sm" />}
                {retrying ? 'Retrying...' : 'Retry'}
            </button>
        </div>
    );

    return (
        <div>
            {!editorReady && <CKEditorLoading />}
            {!editorReady && slowLoading && slowLoadingBanner}
            <div>
                <CKEditor
                    key={`${name}-${retryCount}`}
                    {...(forwardedRef && { ref: forwardedRef })}
                    name={name}
                    readOnly={!!disabled}
                    placeholder={placeholder}
                    initData={
                        previousResponseText ||
                        defaultValue ||
                        (value && typeof value === 'object' ? value.html || value.text || '' : value) ||
                        ''
                    }
                    config={finalConfig}
                    editorUrl={editorUrl}
                    onInstanceReady={handleEditorReady}
                    onChange={handleEditorChange}
                    onBlur={handleBlurEvent}
                />
                {editorReady && (
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px', fontWeight: 'bold' }}>
                        Tip: Enter = line break | Shift+Enter = new paragraph
                    </div>
                )}
            </div>
            <input name={name} type='hidden' value={editorContent} onChange={null} />
        </div>
    )
}

// Custom comparison function for React.memo to ensure re-render when value changes
const arePropsEqual = (prevProps, nextProps) => {
    // Always re-render if value changes
    if (prevProps.value !== nextProps.value) {
        return false;
    }
    // Check other props
    return (
        prevProps.disabled === nextProps.disabled &&
        prevProps.name === nextProps.name &&
        prevProps.placeholder === nextProps.placeholder &&
        prevProps.config === nextProps.config
    );
};

export default React.memo(CustomCKEditor, arePropsEqual);
